import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { getAllProducts } from "../../services/productService";
import { updateProduct } from "../../services/productService";
import { addSale, getAllSales } from "../../services/salesService";
import { getAllCategories } from "../../services/categoryService";
import { getAllUsers, updateUserDetails } from "../../services/userService";
import { addInventoryLog } from "../../services/inventoryLogService";
import { toast } from "react-hot-toast";
import {
    ShoppingCart,
    Plus,
    Minus,
    Trash2,
    Search,
    Tag,
    User,
    Phone,
    Mail,
    Percent,
    CheckCircle,
    ShoppingBag,
    BarChart2,
    X,
    Receipt,
    ChevronRight,
    CreditCard,
} from "lucide-react";
import InvoiceReceipt from "../../components/invoice/InvoiceReceipt";

// ─── helpers ─────────────────────────────────────────────────────────────────
// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) =>
    Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SalesPage() {
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [users, setUsers] = useState([]);
    const [totalSalesCount, setTotalSalesCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [mobileTab, setMobileTab] = useState("products");
    const [activeCategory, setActiveCategory] = useState("All");
    const [productSearch, setProductSearch] = useState("");
    const [cart, setCart] = useState([]);
    const [placing, setPlacing] = useState(false);
    const [successSale, setSuccessSale] = useState(null);
    const [billing, setBilling] = useState({ name: "", mobile: "", email: "" });
    const [matchedUser, setMatchedUser] = useState(null);
    const [lookingUp, setLookingUp] = useState(false);
    const [discountPct, setDiscountPct] = useState("");
    const [paymentType, setPaymentType] = useState("Cash");

    // ── fetch ────────────────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [prods, cats, usrs, sales] = await Promise.all([
                    getAllProducts(),
                    getAllCategories(),
                    getAllUsers(),
                    getAllSales()
                ]);
                setProducts(prods);
                setCategories(cats);
                setUsers(usrs);
                setTotalSalesCount(sales.length);
            } catch {
                toast.error("Failed to load data");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ── mobile lookup ─────────────────────────────────────────────────────────
    const lookupMobile = useCallback(
        (mobile) => {
            if (!mobile || mobile.length < 10) { setMatchedUser(null); return; }
            setLookingUp(true);
            const found = users.find(
                (u) => (u.personalInformation?.mobileNumber || u.personalDetails?.mobile || u.mobileNumber || "") === mobile
            );
            setMatchedUser(found || null);
            if (found) {
                const name = found.personalInformation?.fullName || found.personalDetails?.name || found.displayName || "";
                const email = found.personalInformation?.email || found.personalDetails?.email || found.email || "";
                setBilling((b) => ({ ...b, name: b.name || name, email: b.email || email }));
            }
            setLookingUp(false);
        },
        [users]
    );

    // ── cart ops ──────────────────────────────────────────────────────────────
    const addToCart = (product) => {
        const stock = Number(product.stockCount || 0);
        setCart((prev) => {
            const existing = prev.find((i) => i.id === product.id);
            if (existing) {
                if (existing.qty >= stock) { toast.error("Not enough stock"); return prev; }
                return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
            }
            if (stock === 0) { toast.error("Out of stock"); return prev; }
            return [...prev, {
                id: product.id,
                name: product.name,
                price: Number(product.mrp || product.purchasePrice || 0),
                qty: 1,
                maxQty: stock,
                discount15: Number(product.discount15 || 0),
                discount25: Number(product.discount25 || 0),
                discount35: Number(product.discount35 || 0),
                discount42: Number(product.discount42 || 0),
                discount50: Number(product.discount50 || 0),
            }];
        });
    };

    const changeQty = (id, delta) =>
        setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty: i.qty + delta } : i).filter((i) => i.qty > 0));

    const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));

    const clearCart = () => {
        setCart([]);
        setBilling({ name: "", mobile: "", email: "" });
        setMatchedUser(null);
        setDiscountPct("0");
        setPaymentType("Cash");
    };

    // ── calc ──────────────────────────────────────────────────────────────────
    const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

    // Calculate total discount and total cart value matching exact saved product discounts
    const getDiscountedPrice = (item, pct) => {
        if (pct === "15" && item.discount15) return item.discount15;
        if (pct === "25" && item.discount25) return item.discount25;
        if (pct === "35" && item.discount35) return item.discount35;
        if (pct === "42" && item.discount42) return item.discount42;
        if (pct === "50" && item.discount50) return item.discount50;
        // Fallback mathematical calculation if field is empty
        return item.price * (1 - (Math.min(Number(pct) || 0, 100) / 100));
    };

    const total = cart.reduce((s, i) => s + (getDiscountedPrice(i, discountPct) * i.qty), 0);
    const discountAmt = subtotal - total;
    const cartItemCount = cart.reduce((s, i) => s + i.qty, 0);

    // ── filtered products ─────────────────────────────────────────────────────
    const getCategoryName = (catId) => { const cat = categories.find((c) => c.id === catId); return cat ? cat.name : ""; };

    const filteredProducts = products.filter((p) => {
        const catMatch = activeCategory === "All" || getCategoryName(p.categoryId) === activeCategory;
        const searchMatch = !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.productCode || "").toLowerCase().includes(productSearch.toLowerCase());
        return catMatch && searchMatch && p.isActive !== false && p.status !== "disabled";
    });

    // ── place sale ────────────────────────────────────────────────────────────
    const handlePlaceSale = async () => {
        if (cart.length === 0) { toast.error("Cart is empty"); return; }
        try {
            setPlacing(true);
            const billNumber = totalSalesCount.toString();
            const today = new Date().toISOString().split("T")[0];
            const time = new Date().toLocaleTimeString("en-IN", { hour12: true });
            const saleData = {
                billNumber, date: today, time: time,
                items: cart.map((i) => ({ productId: i.id, productName: i.name, qty: i.qty, unitPrice: i.price, lineTotal: i.price * i.qty })),
                billingPerson: { name: billing.name || "Walk-in Customer", mobile: billing.mobile || "", email: billing.email || "", userId: matchedUser?.id || null },
                subtotal, discountPct: Number(discountPct) || 0, discountAmt, total, paymentType
            };
            const saleId = await addSale(saleData);
            await Promise.all(cart.map(async (item) => {
                const prod = products.find((p) => p.id === item.id);
                if (!prod) return;
                const prev = Number(prod.stockCount || 0);
                const next = Math.max(0, prev - item.qty);
                await updateProduct(item.id, { stockCount: next, sale: Number(prod.sale || 0) + item.qty });
                await addInventoryLog({
                    productId: item.id,
                    productName: item.name,
                    date: today,
                    quantityChange: -item.qty,
                    previousStock: prev,
                    newStock: next,
                    transactionType: `Store Sale - ${paymentType}`,
                    referenceId: billNumber,
                    customerName: saleData.billingPerson.name
                });
            }));
            if (matchedUser) {
                const existing = matchedUser.salesHistory || [];
                await updateUserDetails(matchedUser.id, { salesHistory: [...existing, { billNumber, date: today, time: time, items: saleData.items, total, discountPct: Number(discountPct) || 0, paymentType }] });
            }
            toast.success("Sale placed successfully!");
            setSuccessSale({
                billNo: billNumber,
                createdAt: new Date().toISOString(),
                userName: saleData.billingPerson.name,
                userMobile: saleData.billingPerson.mobile,
                transactionType: "Store Sale",
                products: cart.map(item => ({
                    productName: item.name,
                    quantity: item.qty,
                    price: item.price,
                    mrp: item.price,
                })),
                totalAmount: total,
                paymentMethod: paymentType,
                discountPct: discountPct,
                discountAmt: discountAmt,
            });
            const freshProds = await getAllProducts();
            setProducts(freshProds);
            setTotalSalesCount(prev => prev + 1);
            clearCart();
            setMobileTab("products");
        } catch (err) {
            toast.error("Failed to place sale");
            console.error(err);
        } finally {
            setPlacing(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Reusable JSX blocks (variables, NOT inner components — avoids remount)
    // ─────────────────────────────────────────────────────────────────────────

    const categoryTabsJSX = (
        <div className="bg-white border-b border-gray-200 px-3 pt-3 pb-0 flex-shrink-0">
            <div className="flex gap-1.5 overflow-x-auto pb-3" style={{ scrollbarWidth: "none" }}>
                {["All", ...categories.map((c) => c.name)].map((cat) => (
                    <button key={cat} onClick={() => setActiveCategory(cat)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeCategory === cat ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                    >{cat}</button>
                ))}
            </div>
        </div>
    );

    const productGridJSX = (
        <>
            {/* Search */}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Search products…"
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                </div>
            </div>
            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-3">
                {filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 text-gray-300">
                        <Tag className="w-10 h-10 mb-2 opacity-40" />
                        <p className="text-sm">No products found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                        {filteredProducts.map((product) => {
                            const stock = Number(product.stockCount || 0);
                            const inCart = cart.find((i) => i.id === product.id);
                            return (
                                <button key={product.id} onClick={() => addToCart(product)} disabled={stock === 0}
                                    className={`relative text-left p-3 rounded-2xl border transition-all duration-150 ${stock === 0
                                        ? "bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed"
                                        : inCart
                                            ? "bg-blue-50 border-blue-300 shadow-sm shadow-blue-100"
                                            : "bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm active:scale-95"
                                        }`}
                                >
                                    {inCart && (
                                        <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                            {inCart.qty}
                                        </span>
                                    )}
                                    <div className="w-9 h-9 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center mb-2">
                                        <ShoppingBag className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <p className="text-xs font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">{product.name}</p>
                                    <p className="text-[10px] text-gray-400 mb-1.5">{product.productCode}</p>
                                    <div className="flex items-center justify-between gap-1 flex-wrap">
                                        <span className="text-xs font-bold text-blue-700">₹{fmt(product.mrp || product.purchasePrice || 0)}</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${stock === 0 ? "bg-red-100 text-red-600" : stock <= 5 ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"
                                            }`}>{stock === 0 ? "Out" : `${stock}`}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );

    const cartItemsJSX = (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-300">
                    <ShoppingCart className="w-10 h-10 mb-2" />
                    <p className="text-sm">Cart is empty</p>
                </div>
            ) : (
                cart.map((item) => {
                    const discountedPrice = getDiscountedPrice(item, discountPct);
                    return (
                        <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {Number(discountPct) > 0 ? (
                                        <>
                                            <span className="text-[11px] text-gray-400 line-through">₹{fmt(item.price)}</span>
                                            <span className="text-xs font-semibold text-green-600">₹{fmt(discountedPrice)}</span>
                                            <span className="text-xs text-gray-500">× {item.qty}</span>
                                        </>
                                    ) : (
                                        <span className="text-xs text-gray-500">₹{fmt(item.price)} × {item.qty}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => changeQty(item.id, -1)} className="w-7 h-7 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors">
                                    <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-6 text-center text-sm font-bold">{item.qty}</span>
                                <button onClick={() => item.qty < item.maxQty ? changeQty(item.id, 1) : toast.error("Max stock reached")} className="w-7 h-7 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 flex items-center justify-center transition-colors">
                                    <Plus className="w-3 h-3" />
                                </button>
                            </div>
                            <div className="text-right min-w-[60px]">
                                <p className="text-sm font-bold text-gray-900">₹{fmt(discountedPrice * item.qty)}</p>
                            </div>
                            <button onClick={() => removeFromCart(item.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    );
                })
            )}
        </div>
    );

    const billingFormJSX = (
        <div className="border-t border-gray-100 px-4 py-4 space-y-3 bg-white flex-shrink-0">
            <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Billing Person</p>
                <div className="space-y-2">
                    <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input type="tel" value={billing.mobile}
                            onChange={(e) => setBilling((b) => ({ ...b, mobile: e.target.value }))}
                            onBlur={(e) => lookupMobile(e.target.value)}
                            placeholder="Mobile number"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                        />
                        {lookingUp && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
                    </div>
                    {matchedUser && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-xs text-green-700 font-medium">Known customer — details auto-filled</span>
                        </div>
                    )}
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input type="text" value={billing.name}
                            onChange={(e) => setBilling((b) => ({ ...b, name: e.target.value }))}
                            placeholder="Customer name"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                        />
                    </div>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input type="email" value={billing.email}
                            onChange={(e) => setBilling((b) => ({ ...b, email: e.target.value }))}
                            placeholder="Email (optional)"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                        />
                    </div>
                </div>
            </div>

            {/* Payment Type */}
            <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none font-medium text-gray-700"
                >
                    <option value="Cash">Cash</option>
                    <option value="Gpay">Gpay</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {/* Discount */}
            <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <select
                    value={discountPct}
                    onChange={(e) => setDiscountPct(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 appearance-none font-medium text-gray-700"
                >
                    <option value="0">No Discount (0%)</option>
                    <option value="15">Tier 1 - 15%</option>
                    <option value="25">Tier 2 - 25%</option>
                    <option value="35">Tier 3 - 35%</option>
                    <option value="42">Tier 4 - 42%</option>
                    <option value="50">Tier 5 - 50%</option>
                </select>
                {/* Custom Chevron for select */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                <div className="flex justify-between text-sm text-gray-500"><span>Subtotal</span><span>₹{fmt(subtotal)}</span></div>
                {discountAmt > 0 && (
                    <div className="flex justify-between text-sm text-green-600"><span>Discount ({discountPct}%)</span><span>− ₹{fmt(discountAmt)}</span></div>
                )}
                <div className="flex justify-between font-bold text-base text-gray-900 pt-1.5 border-t border-gray-200">
                    <span>Total</span><span>₹{fmt(total)}</span>
                </div>
            </div>

            {/* Place Sale */}
            <button onClick={handlePlaceSale} disabled={placing || cart.length === 0}
                className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-blue-200 text-sm"
            >
                <Receipt className="w-4 h-4" />
                {placing ? "Processing…" : `Place Sale · ₹${fmt(total)}`}
            </button>
        </div>
    );

    // ── loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading products…</p>
                </div>
            </div>
        );
    }

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
            {/* Top Bar */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-sm z-20">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-base font-bold text-gray-900 leading-tight">Point of Sale</h1>
                        <p className="text-[10px] text-gray-400 hidden sm:block">Select products and checkout</p>
                    </div>
                </div>
                <button onClick={() => navigate("/admin/sales-report")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-semibold hover:bg-indigo-100 transition-colors"
                >
                    <BarChart2 className="w-3.5 h-3.5" /> Report
                </button>
            </div>

            {/* Invoice Modal */}
            {successSale && (
                <InvoiceReceipt
                    transaction={successSale}
                    onClose={() => setSuccessSale(null)}
                />
            )}

            {/* ── MOBILE: Tab bar ── */}
            <div className="lg:hidden flex border-b border-gray-200 bg-white flex-shrink-0">
                <button onClick={() => setMobileTab("products")}
                    className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 ${mobileTab === "products" ? "text-blue-600 border-blue-600" : "text-gray-500 border-transparent"
                        }`}
                >
                    <Tag className="w-4 h-4" /> Products
                </button>
                <button onClick={() => setMobileTab("cart")}
                    className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors border-b-2 ${mobileTab === "cart" ? "text-blue-600 border-blue-600" : "text-gray-500 border-transparent"
                        }`}
                >
                    <ShoppingCart className="w-4 h-4" />
                    Cart
                    {cartItemCount > 0 && (
                        <span className="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartItemCount}</span>
                    )}
                </button>
            </div>

            {/* ── MOBILE: Tab content ── */}
            <div className="lg:hidden flex-1 overflow-hidden flex flex-col">
                {mobileTab === "products" ? (
                    <>
                        {categoryTabsJSX}
                        {productGridJSX}
                        {/* Sticky "Go to Cart" floating bar */}
                        {cart.length > 0 && (
                            <div className="flex-shrink-0 px-3 py-3 bg-white border-t border-gray-100 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
                                <button onClick={() => setMobileTab("cart")}
                                    className="w-full flex items-center justify-between px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-md shadow-blue-200 text-sm"
                                >
                                    <span className="flex items-center gap-2">
                                        <ShoppingCart className="w-4 h-4" />
                                        {cartItemCount} item{cartItemCount !== 1 ? "s" : ""} in cart
                                    </span>
                                    <span className="flex items-center gap-1">₹{fmt(total)} <ChevronRight className="w-4 h-4" /></span>
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Cart header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                                <span className="font-semibold text-gray-900">Cart ({cart.length})</span>
                            </div>
                            {cart.length > 0 && (
                                <button onClick={clearCart} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                                    <Trash2 className="w-3.5 h-3.5" /> Clear
                                </button>
                            )}
                        </div>
                        {cartItemsJSX}
                        {billingFormJSX}
                    </>
                )}
            </div>

            {/* ── DESKTOP: Side-by-side ── */}
            <div className="hidden lg:flex flex-1 overflow-hidden">
                {/* Left — Products */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {categoryTabsJSX}
                    {productGridJSX}
                </div>
                {/* Right — Cart */}
                <div className="w-96 flex flex-col bg-white border-l border-gray-200 shadow-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-gray-900">Cart ({cart.length})</span>
                        </div>
                        {cart.length > 0 && (
                            <button onClick={clearCart} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                                <Trash2 className="w-3.5 h-3.5" /> Clear
                            </button>
                        )}
                    </div>
                    {cartItemsJSX}
                    {billingFormJSX}
                </div>
            </div>
        </div>
    );
}
